import React, { useCallback } from "react";
import type { ChatQueryRequest, ChatQueryResponse } from "@/types/discuss.types";
import type { UseMutateFunction } from "@tanstack/react-query";
import {
  getBackendDocumentId,
  getDocumentTitle,
  notifyDocumentNotReady,
  type BackendDocumentRef,
} from "@/utils/document-id.utils";

const WELCOME_TEMPLATE_LINES = [
  "I am your all round helper equivalent to a senior associate.",
  "",
  "I can review documents, extract information, compare information, do legal research, draft short documents and more.",
  "",
  "**Please provide me with explicit and detailed:**",
  "",
  "*   Context about the situation.",
  "*   Detailed instructions of exactly what you would like me to do.",
  "*   Anything that you don't want me to do (e.g give you tables or bullet points)",
];

export function buildWelcomeMessage(firstName: string | undefined): string {
  const greeting = `**Hello ${firstName ?? "there"}! I'm Amelia,**`;
  return [greeting, "", ...WELCOME_TEMPLATE_LINES].join("\n");
}

export function buildQueryRequest(
  docId: string,
  docTitle: string,
  message: string
): ChatQueryRequest {
  return {
    data: message.trim(),
    document_id: [docId],
    document_title: [docTitle],
    current_document_id: docId,
    audio: false,
  };
}

type SendMessageMutate = UseMutateFunction<ChatQueryResponse, Error, ChatQueryRequest>;

export function useDiscussHandlers(
  doc: BackendDocumentRef | undefined,
  sendMessage: SendMessageMutate,
  onNewMessage?: () => void
) {
  const documentId = getBackendDocumentId(doc);
  const title = getDocumentTitle(doc);

  const handleSendMessage = useCallback(
    async (message: string): Promise<void> => {
      if (message.trim().length === 0) return;
      if (documentId == null) {
        notifyDocumentNotReady("discuss");
        throw new Error("Document isn't ready");
      }
      const queryRequest = buildQueryRequest(documentId, title, message);
      return new Promise<void>((resolve, reject) => {
        sendMessage(queryRequest, {
          onSuccess: () => {
            onNewMessage?.();
            resolve();
          },
          onError: (err) => {
            reject(err);
          },
        });
      });
    },
    [documentId, title, sendMessage, onNewMessage]
  );

  const handleRelatedQuestionClick = useCallback(
    (question: string) => {
      if (documentId == null) {
        notifyDocumentNotReady("discuss");
        return;
      }
      const queryRequest = buildQueryRequest(documentId, title, question);
      sendMessage(queryRequest, {
        onSuccess: () => {
          onNewMessage?.();
        },
      });
    },
    [documentId, title, sendMessage, onNewMessage]
  );

  return { handleSendMessage, handleRelatedQuestionClick };
}

export const markdownComponents = {
  a: (props: React.JSX.IntrinsicElements["a"] & { node?: unknown }) => {
    const { node: _node, ...rest } = props;
    return <a {...rest} target="_blank" rel="noopener noreferrer" />;
  },
};
